// src/screens/DetalleCard/DetalleCard.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useHistory } from 'react-router-dom';
import client from '../../sanity/SanityClient';
import './DetalleCard.css';
import { FiShoppingCart } from 'react-icons/fi';

const STORE = {
  name: 'Taller Juani Pavlovsky',
  addressLine: 'Juncal 2064, CABA',
  openHours: 'Lun a Vie 9–17 hs',
  mapsUrl: 'https://www.google.com/maps?q=Juncal+2064,+CABA',
  whatsapp: '+54 9 11 6003 5550',
  productionDays: 9,
};

const buildWALink = (number, text) =>
  `https://wa.me/${number.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;

const readFavs = () => { try { return new Set(JSON.parse(localStorage.getItem('favoritos') || '[]')); } catch { return new Set(); } };
const saveFavs = (set) => localStorage.setItem('favoritos', JSON.stringify([...set]));

const readCarrito = () => { try { return JSON.parse(localStorage.getItem('carrito') || '[]'); } catch { return []; } };
const writeCarrito = (cart) => { localStorage.setItem('carrito', JSON.stringify(cart)); window.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: cart.length } })); return cart; };

const nf = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });

function DrawerPortal({ open, onClose, children }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = open ? 'hidden' : prev || '';
    return () => { document.body.style.overflow = prev || ''; };
  }, [open]);

  return createPortal(
    <div className={`jpvDrawer ${open ? 'jpvDrawer--open' : ''}`} aria-hidden={!open}>
      <div className="jpvDrawer__backdrop" onClick={onClose} />
      <aside className="jpvDrawer__panel" role="dialog" aria-modal="true">
        {children}
      </aside>
    </div>,
    document.body
  );
}

// envío
function normalize(str = '') { return String(str).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, ''); }
function getShippingZone(provinciaInput = '', localidadInput = '') {
  const provRaw = (provinciaInput || '').trim();
  if (!provRaw) return null;
  const prov = normalize(provinciaInput);
  const loc  = normalize(localidadInput || '');
  const isCABA =
    prov.includes('caba') || prov.includes('ciudad autonoma') || prov.includes('ciudad autónoma') ||
    loc.includes('caba') || loc.includes('capital federal') || loc === 'capital';
  if (isCABA) return { key: 'CABA', cost: 15000 };
  const isPBA = prov.includes('buenos aires') || prov === 'pba';
  if (isPBA) return { key: 'AMBA', cost: 20000 };
  return { key: 'PROV', cost: 25000 };
}

const DetalleCard = () => {
  const { id } = useParams();
  const history = useHistory();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [mainUrl, setMainUrl] = useState('');
  const [thumbs, setThumbs] = useState([]);

  const [size, setSize] = useState('30x45');

  // ===== login flag (para bloquear like / carrito si no está logueado)
  const [isLogged, setIsLogged] = useState(() => {
    try { return !!JSON.parse(localStorage.getItem('user')); } catch { return false; }
  });
  useEffect(() => {
    const h = () => {
      try { setIsLogged(!!JSON.parse(localStorage.getItem('user'))); } catch { setIsLogged(false); }
    };
    window.addEventListener('storage', h);
    return () => window.removeEventListener('storage', h);
  }, []);

  const [favoritos, setFavoritos] = useState(new Set());
  const [showLightbox, setShowLightbox] = useState(false);

  const [toastMsg, setToastMsg] = useState('');
  const showToast = (msg) => { setToastMsg(msg); clearTimeout(window.__toastTimer); window.__toastTimer = setTimeout(() => setToastMsg(''), 1600); };

  useEffect(() => { setFavoritos(readFavs()); }, []);

  useEffect(() => {
    const fetchDoc = async () => {
      setLoading(true); setErr('');
      try {
        const query = `
          *[_type=="foto" && _id==$id][0]{
            _id, titulo, descripcion, categoria,
            enVenta,
            precio30x45, precio50x70,
            "previewUrl": previewImage.asset->url,
            "detalleUrl": detalleImage.asset->url,
            "detalleUrl2": detalleImage2.asset->url,
            "detalleUrl3": detalleImage3.asset->url
          }`;
        const data = await client.fetch(query, { id });
        if (!data) { setErr('No se encontró la obra.'); setDoc(null); setLoading(false); return; }
        setDoc(data);
        const big = data.previewUrl || data.detalleUrl || data.detalleUrl2 || data.detalleUrl3 || '';
        const t = [data.detalleUrl, data.detalleUrl2, data.detalleUrl3].filter(Boolean);
        setMainUrl(big); setThumbs(t);
      } catch (e) { setErr('Error al cargar el detalle'); console.error(e); }
      finally { setLoading(false); }
    };
    fetchDoc();
  }, [id]);

  // switch de venta (si falta el campo, consideramos que sí está a la venta)
  const isForSale = (doc?.enVenta ?? true) === true;

  const price = useMemo(() => {
    if (!doc) return 0;
    const p = size === '30x45' ? doc.precio30x45 : doc.precio50x70;
    const num = typeof p === 'string'
      ? Number(p.replace(/[^\d.,-]/g, '').replace(',', '.'))
      : Number(p);
    return Number.isFinite(num) ? num : 0;
  }, [doc, size]);

  // Drawer / checkout (solo si está a la venta)
  const [showCheckout, setShowCheckout] = useState(false);
  const [qty, setQty] = useState(1);
  const [delivery, setDelivery] = useState('retiro');
  const [cp, setCp] = useState('');
  const [address, setAddress] = useState({ calle:'', numero:'', piso:'', dpto:'', localidad:'', provincia:'' });
  const [phone, setPhone] = useState('');
  const [creatingPref, setCreatingPref] = useState(false);
  const [prefError, setPrefError] = useState('');
  const [payMethod, setPayMethod] = useState('mp'); // 'mp' | 'card' | 'cash'

  // Cupón
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponMsg, setCouponMsg] = useState('');

  const zoneInfo = useMemo(() => getShippingZone(address.provincia, address.localidad), [address.provincia, address.localidad]);
  const shippingCost = useMemo(() => (delivery === 'envio' && zoneInfo ? zoneInfo.cost : 0), [delivery, zoneInfo]);

  const subtotal = price * qty;
  const couponDiscount = appliedCoupon ? Math.round(subtotal * (appliedCoupon.percent / 100)) : 0;
  const total = subtotal + shippingCost - couponDiscount;

  const handleSwapIndex = (idx) => {
    setThumbs(prev => {
      if (idx < 0 || idx >= prev.length) return prev;
      const next = [...prev];
      const clicked = next[idx];
      next[idx] = mainUrl;
      setMainUrl(clicked);
      return next;
    });
  };

  // favoritos (bloqueado si no hay login)
  const handleToggleFav = (e) => {
    e.preventDefault();
    if (!isLogged) { showToast('Iniciá sesión para guardar favoritos'); return; }
    if (!doc?._id) return;
    setFavoritos(prev => {
      const next = new Set(prev);
      if (next.has(doc._id)) next.delete(doc._id); else next.add(doc._id);
      saveFavs(next);
      return next;
    });
  };
  const isFav = doc?._id ? favoritos.has(doc._id) : false;

  // carrito (bloqueado si no hay login)
  const cartItemId = useMemo(() => (doc?._id ? `${doc._id}:${size}` : null), [doc, size]);
  const [inCart, setInCart] = useState(false);
  const syncInCart = useCallback(() => {
    if (!cartItemId) return setInCart(false);
    const cart = readCarrito();
    setInCart(cart.some(it => it.id === cartItemId));
  }, [cartItemId]);
  useEffect(() => { syncInCart(); }, [syncInCart]);
  useEffect(() => {
    const h = () => syncInCart();
    window.addEventListener('cart:updated', h);
    window.addEventListener('storage', h);
    return () => { window.removeEventListener('cart:updated', h); window.removeEventListener('storage', h); };
  }, [syncInCart]);

  const buildCartItem = () => ({ id: `${doc._id}:${size}`, name: doc.titulo || 'Obra', image: mainUrl || doc.previewUrl || doc.detalleUrl || '', price, quantity: 1 });
  const addToCarrito = () => {
    if (!isLogged) { showToast('Iniciá sesión para usar el carrito'); return; }
    const item = buildCartItem(); const cart = readCarrito(); const idx = cart.findIndex(it => it.id === item.id);
    if (idx >= 0) { cart[idx].quantity = Math.max(1, (cart[idx].quantity || 1) + 1); writeCarrito(cart); }
    else { writeCarrito([...cart, item]); }
    setInCart(true); showToast('Agregado al carrito');
  };
  const removeFromCarrito = () => { const cart = readCarrito().filter(it => it.id !== cartItemId); writeCarrito(cart); setInCart(false); showToast('Quitado del carrito'); };
  const addToCart = () => { if (!doc) return; inCart ? removeFromCarrito() : addToCarrito(); };
  const handleComprar = () => { setPrefError(''); setShowCheckout(true); };

  // WhatsApp: mensajes
  const waMsgNoVenta =
    `Hola Juani 👋 Quiero consultarte por la fotografía "${doc?.titulo || 'Obra'}".\n` +
    `Link: ${window.location.origin}/detalle/${doc?._id}`;
  const waLinkNoVenta = buildWALink(STORE.whatsapp, waMsgNoVenta);

  const waMessageEnmarcado =
    `Hola Juani 👋 Consulto por enmarcado.\n` +
    `Obra: ${doc?.titulo || 'Obra'} (${size})\n` +
    `Entrega: ${delivery === 'retiro' ? 'Retiro en tienda' : 'Envío a domicilio'}\n` +
    `Total estimado: ${nf.format((price * qty) + shippingCost)}`;
  const waLinkEnmarcado = buildWALink(STORE.whatsapp, waMessageEnmarcado);

  const applyCoupon = async () => {
    const code = (couponInput || '').trim();
    setCouponMsg('');
    if (!code) return;
    try {
      const resp = await fetch('/api/discount/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      const data = await resp.json();
      if (!resp.ok || !data?.valid) throw new Error(data?.message || 'Cupón inválido');
      setAppliedCoupon({ code: data.code, percent: Number(data.percent || 0) });
      setCouponMsg(`Cupón aplicado: ${data.code} (-${data.percent}%)`);
    } catch (e) {
      setAppliedCoupon(null);
      setCouponMsg(e?.message || 'Cupón inválido');
    }
  };

  const removeCoupon = () => { setAppliedCoupon(null); setCouponInput(''); setCouponMsg('Cupón quitado'); };

  const finalizarCompra = async () => {
    if (!doc) return;
    setCreatingPref(true);
    setPrefError('');

    try {
      const titleBase = `${doc.titulo || 'Obra'} (${size}) x${qty}`;
      const TOTAL = Math.max(1, Math.round(total));

      const resp = await fetch('/api/mp/create-preference', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: titleBase,
          picture_url: mainUrl || doc.previewUrl || '',
          quantity: 1,
          unit_price: TOTAL,
          size,
          delivery,
          shippingCost: Math.round(Number(shippingCost || 0)),
          address: { cp, telefono: phone, ...address },
          buyerEmail: localStorage.getItem('authEmail') || '',
          payMethod,
          metadata: {
            unit: Math.round(Number(price || 0)),
            qty,
            coupon: appliedCoupon || null
          }
        })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || 'Fallo al crear preference');
      const urlToPay = data.init_point || data.sandbox_init_point;
      if (!urlToPay) throw new Error('Respuesta sin init_point');
      window.location.assign(urlToPay);
    } catch (e) {
      console.error(e);
      setPrefError(e?.message || 'No se pudo iniciar el pago');
    } finally {
      setCreatingPref(false);
    }
  };

  const badgeCount = thumbs.length;

  // ===== padding dinámico para que el botón sticky no tape contenido
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 900px)').matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const h = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', h); else mq.addListener(h);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', h); else mq.removeListener(h); };
  }, []);
  const bodyPadBottom = isMobile
    ? 'calc(28px + env(safe-area-inset-bottom))'
    : 'calc(10px + env(safe-area-inset-bottom))';

  if (loading) return <div className="detalle-wrapper"><p className="detalle-info">Cargando…</p></div>;
  if (err) return <div className="detalle-wrapper"><p className="detalle-info">{err}</p></div>;
  if (!doc) return null;

  return (
    <div className="detalle-wrapper" onContextMenu={(e) => e.preventDefault()}>
      <div className="detalle-grid">
        {/* Thumbs */}
        <div className="thumb-col">
          {thumbs.length === 0 ? (
            <div className="thumb-wrap thumb-empty" title="Sin imágenes de detalle">
              <div className="thumb-empty-box" />
              <span className="thumb-overlay" />
              <span className="thumb-badge">+{badgeCount}</span>
            </div>
          ) : (
            thumbs.map((url, idx) => (
              <button key={idx} type="button" className="thumb-wrap" onClick={() => handleSwapIndex(idx)} title="Ver otra imagen">
                <img src={url} alt={`Miniatura ${idx + 1}`} className="thumb-img" loading="lazy" draggable="false" onDragStart={(e) => e.preventDefault()} />
                <span className="thumb-overlay" />
                {idx === 0 && <span className="thumb-badge">+{badgeCount}</span>}
              </button>
            ))
          )}
        </div>

        {/* Main */}
        <div className="main-col">
          {mainUrl ? (
            <button type="button" className="main-img-wrap" onClick={() => setShowLightbox(true)} title="Ampliar">
              <img src={mainUrl} alt={doc.titulo || 'Obra'} className="main-img" draggable="false" onDragStart={(e) => e.preventDefault()} />
            </button>
          ) : (
            <div className="main-placeholder">Sin imagen</div>
          )}
        </div>

        {/* Info */}
        <div className="info-col">
          <div className="title-row">
            <h1 className="detalle-title">{doc.titulo || 'Obra'}</h1>
            <button
              type="button"
              className={`fav-btn ${isFav ? 'is-fav' : ''}`}
              onClick={handleToggleFav}
              aria-label={isFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}
              title={isFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}
              disabled={!isLogged}
            >
              {isFav ? (
                <svg className="ri-heart-fill" viewBox="0 0 24 24" aria-hidden="true"><path d="M12.001 4.529c2.349-2.109 5.979-2.04 8.242.228 2.263 2.268 2.34 5.88.236 8.236L12 21.485 3.521 12.993c-2.104-2.356-2.026-5.974.236-8.236 2.264-2.264 5.888-2.34 8.244-.228z" /></svg>
              ) : (
                <svg className="ri-heart-line" viewBox="0 0 24 24" aria-hidden="true"><path d="M12.001 4.52853C14.35 2.42 17.98 2.49 20.2426 4.75736C22.5053 7.02472 22.583 10.637 20.4786 12.993L11.9999 21.485L3.52138 12.993C1.41705 10.637 1.49571 7.01901 3.75736 4.75736C6.02157 2.49315 9.64519 2.41687 12.001 4.52853ZM18.827 6.1701C17.3279 4.66794 14.9076 4.60701 13.337 6.01687L12.0019 7.21524L10.6661 6.01781C9.09098 4.60597 6.67506 4.66808 5.17157 6.17157C3.68183 7.66131 3.60704 10.0473 4.97993 11.6232L11.9999 18.6543L19.0201 11.6232C20.3935 10.0467 20.319 7.66525 18.827 6.1701Z" /></svg>
              )}
            </button>
          </div>

          {/* Precio / estado de venta */}
          <div className="price-row">
            <span className="price-label">Precio</span>
            {isForSale ? (
              <span className="price-value">{nf.format(price)}</span>
            ) : (
              <span className="price-value price--no-sale">No está en venta</span>
            )}
          </div>

          {/* Contacto si NO está a la venta */}
          {!isForSale && (
            <div className="price-contact">
              <small>Contactar para más detalles </small>
              <a className="wa-cta" href={waLinkNoVenta} target="_blank" rel="noreferrer">
                <strong>Escribir por WhatsApp</strong>
              </a>
            </div>
          )}

          {/* Medidas */}
          {isForSale && (
            <div className="size-row">
              <span className="size-label">Medidas:</span>
              <div className="size-toggle" role="tablist" aria-label="Elegir medidas">
                <button type="button" className={`size-btn ${size === '30x45' ? 'active' : ''}`} onClick={() => setSize('30x45')} role="tab" aria-selected={size === '30x45'}>30×45</button>
                <button type="button" className={`size-btn ${size === '50x70' ? 'active' : ''}`} onClick={() => setSize('50x70')} role="tab" aria-selected={size === '50x70'}>50×70</button>
              </div>
            </div>
          )}

          {/* Acciones */}
          {isForSale && (
            <div className="actions">
              <button
                type="button"
                className={`btn-cart ${inCart ? 'is-in-cart' : ''}`}
                onClick={addToCart}
                disabled={!isLogged}
                title={isLogged ? '' : 'Iniciá sesión para usar el carrito'}
              >
                <FiShoppingCart size={18} /><span>{inCart ? 'Sacar del carrito' : 'Agregar al carrito'}</span>
              </button>
              <button type="button" className="btn-buy" onClick={handleComprar}>Comprar</button>
            </div>
          )}

          {doc.descripcion ? (
            <div className="desc">
              <h3>Descripción</h3>
              <p className="desc-text">{doc.descripcion}</p>
            </div>
          ) : null}

          <details className="acc">
            <summary>Medios de pago</summary>
            <div className="acc-body">Mercado Pago <br /> Tarjetas <br /> Efectivo</div>
          </details>
          <details className="acc">
            <summary>Medios de envío</summary>
            <div className="acc-body">
              <ul>
                <li>Punto de Retiro (sin costo). Horarios: {STORE.openHours}.</li>
                <li>Correo/Carrier a domicilio (CABA, AMBA y resto del país).</li>
              </ul>
            </div>
          </details>
          <details className="acc">
            <summary>Cambios y devoluciones</summary>
            <div className="acc-body">
              En breve: términos y condiciones de la compra (<button type="button" className="link-legal" onClick={() => history.push('/legal/copyright')}>ver sección legal</button>).
            </div>
          </details>
        </div>
      </div>

      {/* Drawer */}
      {isForSale && (
        <DrawerPortal open={showCheckout} onClose={() => setShowCheckout(false)}>
          <div className="jpvDrawer__header">
            <strong>Compra</strong>
            <button className="jpvDrawer__close" onClick={() => setShowCheckout(false)} aria-label="Cerrar">×</button>
          </div>

          {/* BODY con padding-bottom dinámico (compacto + safe-area) */}
          <div className="jpvDrawer__body" style={{ paddingBottom: bodyPadBottom }}>
            {/* Mini resumen con contador */}
            <div className="mini-item">
              <img src={mainUrl || doc?.previewUrl || ''} alt="thumb" />
              <div className="mini-info">
                <div className="mini-title">{doc?.titulo || 'Obra'}</div>
                <div className="mini-sub">{size}</div>
                <div className="mini-price">{nf.format(price)}</div>
              </div>
              <div className="qty">
                <button onClick={() => setQty(q => Math.max(1, q - 1))} aria-label="menos">−</button>
                <span>{qty}</span>
                <button onClick={() => setQty(q => q + 1)} aria-label="más">+</button>
              </div>
            </div>

            {/* Entrega */}
            <div className="section">
              <div className="section-title">Entrega</div>
              <div className="toggle-group" role="tablist" aria-label="Elegir entrega">
                <button type="button" className={`size-btn ${delivery === 'retiro' ? 'active' : ''}`} role="tab" aria-selected={delivery === 'retiro'} onClick={() => setDelivery('retiro')}>Retiro en tienda (sin costo)</button>
                <button type="button" className={`size-btn ${delivery === 'envio' ? 'active' : ''}`} role="tab" aria-selected={delivery === 'envio'} onClick={() => setDelivery('envio')}>Envío a domicilio</button>
              </div>

              {delivery === 'retiro' && (
                <div className="card card-soft">
                  <div className="card-title">{STORE.name}</div>
                  <div className="card-sub">{STORE.addressLine}</div>
                  <div className="card-sub">Horarios: {STORE.openHours}</div>
                  <div className="card-note">⏱ Se puede retirar {STORE.productionDays} días después de la compra.</div>
                  <a className="link-arrow" href={STORE.mapsUrl} target="_blank" rel="noreferrer">Ver ubicación en Google Maps</a>
                </div>
              )}

              {delivery === 'envio' && (
                <div className="card card-soft">
                  <div className="shipping-form">
                    <div className="row inline">
                      <label>Código postal</label>
                      <input value={cp} onChange={e => setCp(e.target.value)} placeholder="Ej: 1425" />
                    </div>
                    <div className="row two">
                      <div><label>Calle</label><input value={address.calle} onChange={e => setAddress(a => ({ ...a, calle: e.target.value }))} /></div>
                      <div><label>Número</label><input value={address.numero} onChange={e => setAddress(a => ({ ...a, numero: e.target.value }))} /></div>
                    </div>
                    <div className="row two">
                      <div><label>Piso</label><input value={address.piso} onChange={e => setAddress(a => ({ ...a, piso: e.target.value }))} /></div>
                      <div><label>Dpto</label><input value={address.dpto} onChange={e => setAddress(a => ({ ...a, dpto: e.target.value }))} /></div>
                    </div>
                    <div className="row two">
                      <div><label>Localidad</label><input value={address.localidad} onChange={e => setAddress(a => ({ ...a, localidad: e.target.value }))} /></div>
                      <div><label>Provincia</label><input value={address.provincia} onChange={e => setAddress(a => ({ ...a, provincia: e.target.value }))} /></div>
                    </div>
                    <div className="row inline">
                      <label>Teléfono</label>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Ej: 11 2345-6789" />
                    </div>
                  </div>

                  <div className="sep" />
                  <div className="row-between"><span>Costo de envío</span><strong>{zoneInfo ? nf.format(shippingCost) : '—'}</strong></div>
                  <small className="muted">{zoneInfo ? 'El envío se suma a los 9 días de producción.' : 'Ingresá provincia y localidad para calcular el costo.'}</small>
                </div>
              )}
            </div>

            {/* Pago */}
            <div className="section">
              <div className="section-title">Pago</div>
              <div className="toggle-group" role="tablist" aria-label="Elegir forma de pago">
                <button type="button" className={`size-btn ${payMethod === 'mp' ? 'active' : ''}`} role="tab" aria-selected={payMethod === 'mp'} onClick={() => setPayMethod('mp')}>Mercado Pago (billetera)</button>
                <button type="button" className={`size-btn ${payMethod === 'card' ? 'active' : ''}`} role="tab" aria-selected={payMethod === 'card'} onClick={() => setPayMethod('card')}>Tarjeta</button>
                <button type="button" className={`size-btn ${payMethod === 'cash' ? 'active' : ''}`} role="tab" aria-selected={payMethod === 'cash'} onClick={() => setPayMethod('cash')}>Efectivo</button>
              </div>

              {/* Cupón */}
              <div className="coupon-box">
                <label htmlFor="coupon">Código de descuento</label>
                <div className="coupon-row">
                  <input id="coupon" value={couponInput} onChange={(e) => setCouponInput(e.target.value)} className='input' />
                  <button type="button" onClick={applyCoupon}>Aplicar</button>
                  {appliedCoupon && <button type="button" className="link-btn" onClick={removeCoupon}>Quitar cupón</button>}
                </div>
                {couponMsg && <small className="muted">{couponMsg}</small>}
                {appliedCoupon && <small className="muted">Aplicado: {appliedCoupon.code} (−{appliedCoupon.percent}%)</small>}
              </div>
            </div>

            {/* Totales */}
            <div className="totals">
              <div><span>Subtotal</span><span>{nf.format(subtotal)}</span></div>
              {appliedCoupon && <div><span>Descuento cupón ({appliedCoupon.percent}%)</span><span>-{nf.format(couponDiscount)}</span></div>}
              <div><span>Envío</span><span>{delivery === 'envio' ? (zoneInfo ? nf.format(shippingCost) : '—') : 'Sin costo'}</span></div>
              <div className="grand"><span>Total</span><span>{nf.format(total)}</span></div>
            </div>

            {prefError && <div className="error-box">{prefError}</div>}

            {/* WhatsApp enmarcado */}
            <div className="wa-box">
              <a className="btn-wa" href={waLinkEnmarcado} target="_blank" rel="noreferrer">Consultar enmarcado por WhatsApp</a><br />
              <small className="wa-hint">El enmarcado se cotiza y paga aparte.</small>
            </div>
          </div>

          {/* Footer sticky SOLO con el botón, sin fondo y con safe-area */}
          <div
            className="drawer-sticky"
            style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 5,
              padding: '4px 12px',
              paddingBottom: 'calc(4px + env(safe-area-inset-bottom))'
            }}
          >
            <button
              className="btn-primary"
              onClick={finalizarCompra}
              disabled={
                creatingPref ||
                (delivery === 'envio' && (!cp || !address.calle || !address.numero || !address.localidad || !address.provincia || !zoneInfo || phone.trim().length < 8))
              }
            >
              {creatingPref ? 'Creando pago…' : 'Finalizar compra'}
            </button>
          </div>
        </DrawerPortal>
      )}

      {/* Lightbox */}
      <div className={`lightbox ${showLightbox ? 'open' : ''}`} aria-hidden={!showLightbox}>
        <div className="lightbox-backdrop" onClick={() => setShowLightbox(false)} />
        <div className="lightbox-body">
          <button className="lightbox-close" onClick={() => setShowLightbox(false)} aria-label="Cerrar">×</button>
          {mainUrl && (
            <img src={mainUrl} alt={doc?.titulo || 'Obra'} className="lightbox-img" draggable="false" onDragStart={(e) => e.preventDefault()} onContextMenu={(e) => e.preventDefault()} />
          )}
        </div>
      </div>

      {/* Toast */}
      {toastMsg && <div className="toast" role="status" aria-live="polite">{toastMsg}</div>}
    </div>
  );
};

export default DetalleCard;
